import { createAccessControl } from "better-auth/plugins/access";
import { adminAc, defaultStatements } from "better-auth/plugins/admin/access";

/**
 * What a CI portal role may do. Shared by the API (src/api) and
 * the web app (src/web), so a button is only shown when the API allows it.
 * `user` and `session` come from Better Auth's admin plugin.
 */
export const ciStatements = {
  ...defaultStatements,
  mock: ["read", "write", "delete"],
} as const;

export const ciAccessControl = createAccessControl(ciStatements);

/** Every signed-in tenant user: read-only. */
export const viewerRole = ciAccessControl.newRole({
  mock: ["read"],
});

/** Creates and edits mocks. */
export const editorRole = ciAccessControl.newRole({
  mock: ["read", "write", "delete"],
});

/** Everything, including user management. */
export const adminRole = ciAccessControl.newRole({
  mock: ["read", "write", "delete"],
  ...adminAc.statements,
});

export const ciRoles = {
  viewer: viewerRole,
  editor: editorRole,
  admin: adminRole,
};

/** Name of a CI portal role. */
export type CiRoleName = keyof typeof ciRoles;

export const CI_ROLE_NAMES: CiRoleName[] = ["viewer", "editor", "admin"];

export const DEFAULT_CI_ROLE: CiRoleName = "viewer";

export const isCiRoleName = (value: unknown): value is CiRoleName =>
  typeof value === "string" && (CI_ROLE_NAMES as string[]).includes(value);

type CiRoleCanProps = {
  /** Role string from the session (Better Auth allows comma-separated roles) */
  role: string | null | undefined;
  /** Resource and action to check, e.g. `{ mock: ["write"] }` */
  permissions: {
    [K in keyof typeof ciStatements]?: (typeof ciStatements)[K][number][];
  };
};

/** Whether any of the user's roles grants every listed permission. */
export const ciRoleCan = ({ role, permissions }: CiRoleCanProps): boolean => {
  const names = (role ?? DEFAULT_CI_ROLE).split(",").map((name) => name.trim());
  return names.some(
    (name) =>
      isCiRoleName(name) && ciRoles[name].authorize(permissions).success,
  );
};
